/// <amd-module name="Wit/WorkItemModel/WorkItem/Links" />
import { format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { CoreField, DalFields, WorkItemLinkConstants } from "Wit/Common/Generated/Constants";
import { FutureDate } from "Wit/Common/WorkItemControls/Constants";
import { ILinkInfo, IWorkItemLinkType, IWorkItemLinkTypeEnd } from "Wit/WorkItemDataManager/Contracts";
import { IWorkItemDataManager } from "Wit/WorkItemDataManager/WorkItemDataManager";
import {  RegisteredLinkTypeNames } from "Wit/WorkItemModel/OM";
import { ILink, IWorkItem, RemoteLinkContext } from "Wit/WorkItemModel/OM/WorkItemInterfaces";
import * as Resources from "Wit/WorkItemModel/Resources";
import { getWorkItemUrl, isRemoved } from "Wit/WorkItemModel/Utils";

/**
 * This is the date to use for end dates of active items.
 */
export class Link implements ILink {
    public static createFromLinkData(linkData: ILinkInfo) {
        linkData = linkData || ({} as ILinkInfo);

        if (!linkData.FldID) {
            // From Files table
            linkData.FldID = DalFields.RelatedLinks;
        }

        const fieldId = linkData.FldID;

        switch (fieldId) {
            case DalFields.AttachedFiles:
                return new Attachment(linkData);
            case DalFields.LinkedFiles:
                return new Hyperlink(linkData);
            case DalFields.BISURI:
                return new ExternalLink(linkData);
            case DalFields.RelatedLinks:
                return new WorkItemLink(linkData);
            default:
                return new Link(linkData);
        }
    }

    public static getCountFieldId(linkFieldId: number): number {
        let countFieldId;
        switch (linkFieldId) {
            case DalFields.AttachedFiles:
                countFieldId = CoreField.AttachedFileCount;
                break;
            case DalFields.RelatedLinks:
                countFieldId = CoreField.RelatedLinkCount;
                break;
            case DalFields.LinkedFiles:
                countFieldId = CoreField.HyperLinkCount;
                break;
            case DalFields.BISURI:
                countFieldId = CoreField.ExternalLinkCount;
                break;
            default:
                countFieldId = 0;
        }
        return countFieldId;
    }

    public linkData: ILinkInfo;

    public pendingUpdate: boolean;
    public pendingDeletion: boolean;

    public remoteHostId?: string;
    public remoteHostName?: string;
    public remoteHostUrl?: string;
    public remoteProjectId?: string;
    public baseLinkType: string;
    public isLocked: boolean;
    public comment?: string;

    constructor(linkData: ILinkInfo) {
        this.linkData = linkData;

        this.pendingUpdate = false;
        this.pendingDeletion = false;
        this.baseLinkType = "Link";
        this.remoteHostId = this.linkData.RemoteHostId;
    }

    /**
     * Sets Link updated state
     *
     * @param flushing: Set to true if to flush the Link object with associated LinkData and reset all changed flags to false
     */
    public setState(flushing?: boolean): void {
        if (flushing) {
            this.comment = this.getComment();
            this.isLocked = this.getIsLocked();

            this.pendingUpdate = false;
            this.linkData.CommentChanged = false;
            this.linkData.LockChanged = false;
        } else {
            this.pendingUpdate = this.linkData.CommentChanged || this.linkData.LockChanged || false;
        }
    }

    /**
     * Compares this link to the link provided.
     * @param link Link to compare against this link.
     * @return True if they are equal and false otherwise.
     */
    public equals(link: ILink): boolean {
        if (!link) {
            return false;
        }

        if (this.baseLinkType !== link.baseLinkType) {
            return false;
        }

        return true;
    }

    public isDuplicate(link: Link): boolean {
        return this.equals(link);
    }

    public isNew(): boolean {
        const addedDate = this.getAddedDate();
        return !addedDate || addedDate.getTime() === FutureDate.getTime();
    }

    public isRemoved(): boolean {
        const removedDate = this.getRemovedDate();
        return isRemoved(removedDate);
    }

    public isRemote(): boolean {
        return false;
    }

    public getAddedDate(): Date | undefined {
        return this.linkData.AddedDate;
    }

    public getRemovedDate(): Date | undefined {
        return this.linkData.RemovedDate;
    }

    public getFieldId(): number {
        return this.linkData.FldID || -1;
    }

    public getComment(): string | undefined {
        return this.linkData.Comment;
    }

    public setComment(comment?: string, preventChangeEvent?: boolean) {
        this.linkData.Comment = comment;
        this.linkData.CommentChanged = true;
        this.setState();

        if (!preventChangeEvent) {
            // this._fireChanged();
        }
    }

    public getIsLocked(): boolean {
        return this.linkData.Lock || false;
    }

    public remove(options?: { preventEvent?: boolean }) {
        const preventEvent = options && options.preventEvent === true;
        this.pendingDeletion = true;

        if (!preventEvent) {
            // TODO:Replat fire events
            // this.workItem.resetLinks(true);
            // this._fireChanged();
        }
    }

    public async getRelationType(context: IVssPageContext): Promise<string> {
        switch (this.linkData.FldID) {
            case DalFields.BISURI:
                return WorkItemLinkConstants.ARTIFACTLINKTYPE;
            case DalFields.AttachedFiles:
                return WorkItemLinkConstants.ATTACHEDLINKTYPE;
            case DalFields.LinkedFiles:
                return WorkItemLinkConstants.HYPERLINKLINKTYPE;
            default:
                return this instanceof WorkItemLink ? 
                    (await this.getWitLinkTypeEnd(context)).immutableName 
                    : RegisteredLinkTypeNames.Related;
        }
    }

    public getTargetId(): number {
        return this.linkData.ID || -1;
    }

    public getLinkType() {
        return this.linkData.OriginalName ?? RegisteredLinkTypeNames.Related;
    }

    public getWitLinkTypeEndId() {
        return this.linkData.LinkType ?? undefined;
    }

    public async clone(context: IVssPageContext, target: IWorkItem): Promise<Link | undefined> {
        return undefined;
    }

    public getAttributes(): { [key: string]: boolean | number | Date | string | undefined } {
        const attributes: { [key: string]: boolean | number | Date | string | undefined } = {
            isDeleted: this.pendingDeletion,
            isNew: this.isNew()
        };

        if (this._isResourceLinkType(this.linkData.FldID || 0)) {
            attributes[WorkItemLinkConstants.ATTRIBUTES_AUTHORIZEDDATE] = this.linkData.AddedDate;
            attributes[WorkItemLinkConstants.ATTRIBUTES_ID] = this.linkData.ExtID;
            attributes[WorkItemLinkConstants.ATTRIBUTES_RESOURCECREATEDDATE] = this.linkData.CreationDate;
            attributes[WorkItemLinkConstants.ATTRIBUTES_RESOURCEMODIFIEDDATE] = this.linkData.LastWriteDate;
            attributes[WorkItemLinkConstants.ATTRIBUTES_REVISEDDATE] = this.linkData.RemovedDate;

            if (this.linkData.Length && this.linkData.Length > 0) {
                attributes[WorkItemLinkConstants.ATTRIBUTES_RESOURCESIZE] = this.linkData.Length;
            }

            if (this.linkData.Comment) {
                attributes[WorkItemLinkConstants.ATTRIBUTES_COMMENT] = this.linkData.Comment;
            }

            if (this.linkData.OriginalName) {
                attributes[WorkItemLinkConstants.ATTRIBUTES_NAME] = this.linkData.OriginalName;
            }

            return attributes;
        } else if (this.linkData.FldID === DalFields.RelatedLinks) {
            attributes[WorkItemLinkConstants.ATTRIBUTES_ISLOCKED] = this.linkData.Lock;

            if (this.linkData.Comment) {
                attributes[WorkItemLinkConstants.ATTRIBUTES_COMMENT] = this.linkData.Comment;
            }

            return attributes;
        }

        return {};
    }

    public async getLinkUrl(pageContext: IVssPageContext, projectId: string): Promise<string> {
        if (this._isResourceLinkType(this.linkData.FldID || 0)) {
            return this.linkData.FilePath || "";
        } else if (this.remoteHostId) {
            return this._getRemoteWorkItemRestUrl(this.remoteHostUrl || "", this.remoteProjectId || "", this.linkData.ID || 0);
        } else {
            return await getWorkItemUrl(pageContext, projectId, this.linkData.ID || 0);
        }
    }

    private _isResourceLinkType(linkFieldID: number): boolean {
        switch (linkFieldID) {
            case DalFields.BISURI:
            case DalFields.AttachedFiles:
            case DalFields.LinkedFiles:
                return true;
            default:
                return false;
        }
    }

    private _getRemoteWorkItemRestUrl(remoteHostUrl: string, projectName: string, workItemId: number): string {
        return `${remoteHostUrl}/${projectName}/_apis/wit/workItems/${workItemId}`;
    }
}

export class WorkItemLink extends Link {
    private static linkTypeEnds: IWorkItemLinkTypeEnd[];

    public static async create(
        context: IVssPageContext,
        linkTypeName: string,
        targetId: number,
        comment?: string,
        addedBySystem?: boolean,
        remoteLinkContext?: RemoteLinkContext
    ): Promise<WorkItemLink>;
    public static async create(
        context: IVssPageContext,
        linkTypeId: number,
        targetId: number,
        comment?: string,
        addedBySystem?: boolean,
        remoteLinkContext?: RemoteLinkContext
    ): Promise<WorkItemLink>;
    public static async create(
        context: IVssPageContext,
        linkTypeEnd: IWorkItemLinkTypeEnd,
        targetId: number,
        comment?: string,
        addedBySystem?: boolean,
        remoteLinkContext?: RemoteLinkContext
    ): Promise<WorkItemLink>;
    public static async create(
        context: IVssPageContext,
        linkTypeEndOrId: any,
        targetId: number,
        comment?: string,
        addedBySystem?: boolean,
        remoteLinkContext?: RemoteLinkContext
    ): Promise<WorkItemLink> {
        let linkTypeId: number;

        if (typeof linkTypeEndOrId === "object") {
            linkTypeId = (linkTypeEndOrId as IWorkItemLinkTypeEnd).id;
        } else {
            const linkTypeEnd = await WorkItemLink.findLinkTypeEnd(context, linkTypeEndOrId);
            linkTypeId = linkTypeEnd.id;
        }

        return WorkItemLink.createSync(linkTypeId, targetId, comment, addedBySystem, remoteLinkContext);
    }

    public static createSync(
        linkTypeId: number,
        targetId: number,
        comment?: string,
        addedBySystem?: boolean,
        remoteLinkContext?: RemoteLinkContext
    ): WorkItemLink {
        return new WorkItemLink({
            ID: targetId,
            LinkType: linkTypeId,
            Comment: comment || "",
            FldID: DalFields.RelatedLinks,
            isAddedBySystem: addedBySystem,
            RemoteHostUrl: remoteLinkContext && remoteLinkContext.remoteHostUrl,
            RemoteProjectId: remoteLinkContext && remoteLinkContext.remoteProjectId,
            RemoteHostId: remoteLinkContext && remoteLinkContext.remoteHostId,
            RemoteHostName: remoteLinkContext && remoteLinkContext.remoteHostName
        });
    }

    public static async findLinkTypeEnd(context: IVssPageContext, idOrName: number | string): Promise<IWorkItemLinkTypeEnd> {
        const search = ("" + idOrName).toUpperCase();

        const linkTypeEnds = await this.getLinkTypeEnds(context);
        for (const linkTypeEnd of linkTypeEnds) {
            if (linkTypeEnd.id === idOrName) {
                return linkTypeEnd;
            } else if (linkTypeEnd.name.toUpperCase() === search) {
                return linkTypeEnd;
            } else if (linkTypeEnd.immutableName.toUpperCase() === search) {
                return linkTypeEnd;
            }
        }

        throw Error(format(Resources.WorkItemLinkTypeEndDoesNotExist, idOrName));
    }

    public static async getLinkTypes(context: IVssPageContext) {
        const widm = context.getService<IWorkItemDataManager>("IWorkItemDataManager");
        const linkTypes = await widm.beginGetLinkTypes();
        if (!linkTypes) {
            throw Error(); // TODO
        }

        this._bindLinkTypeEnds(linkTypes.witLinkTypes);

        // Right now, artifact link types are coupled with WIT service code. We have to register all the link types in the
        // servicing step "Register WorkItemTracking Artifact Types" even if it is contributed by an extension.
        // Identify such link types and filter them out. When coupling is removed, the filtering code below should also be removed.
        // this.registeredLinkTypes = linkTypes.registeredLinkTypes.filter(t => isLinkFormRegistered(t));

        return linkTypes.witLinkTypes;
    }

    private static async getLinkTypeEnds(context: IVssPageContext): Promise<IWorkItemLinkTypeEnd[]> {
        let linkTypeEnds = this.linkTypeEnds;
        if (linkTypeEnds) {
            return linkTypeEnds;
        }

        const linkTypes = await WorkItemLink.getLinkTypes(context);
        linkTypeEnds = [];

        for (const linkType of linkTypes) {
            linkTypeEnds.push(linkType.forwardEnd);

            if (linkType.isDirectional) {
                linkTypeEnds.push(linkType.reverseEnd);
            }
        }

        this.linkTypeEnds = linkTypeEnds;

        return linkTypeEnds;
    }

    private static _bindLinkTypeEnds(linkTypes: IWorkItemLinkType[]) {
        let linkTypeEnd: IWorkItemLinkTypeEnd;

        for (const linkType of linkTypes) {
            linkTypeEnd = linkType.forwardEnd;
            if (linkTypeEnd) {
                linkTypeEnd.linkType = linkType;
                linkTypeEnd.oppositeEnd = linkType.reverseEnd;
            }

            linkTypeEnd = linkType.reverseEnd;
            if (linkTypeEnd) {
                linkTypeEnd.linkType = linkType;
                linkTypeEnd.oppositeEnd = linkType.forwardEnd;
            }
        }
    }

    private _linkTypeEnd: IWorkItemLinkTypeEnd;

    constructor(linkData: ILinkInfo) {
        super(linkData);

        this.baseLinkType = "WorkItemLink";
    }

    public equals(link: ILink) {
        return (
            super.equals(link) &&
            link instanceof WorkItemLink &&
            link.linkData.ID === this.linkData.ID &&
            link.linkData.LinkType === this.linkData.LinkType
        );
    }

    public isRemote(): boolean {
        return !!this.remoteHostId;
    }

    public getAddedDate(): Date | undefined {
        return this.linkData["Changed Date"];
    }

    public getRemovedDate(): Date | undefined {
        // This is, unfortunately, the correct key.
        return this.linkData["Revised Date"];
    }

    public getLinkType(): string {
        return RegisteredLinkTypeNames.Related;
    }

    public async getWitLinkTypeEnd(context: IVssPageContext): Promise<IWorkItemLinkTypeEnd> {
        if (!this._linkTypeEnd) {
            this._linkTypeEnd = await WorkItemLink.findLinkTypeEnd(context, this.linkData.LinkType!);
        }

        return this._linkTypeEnd;
    }

    public async clone(context: IVssPageContext, target: IWorkItem): Promise<WorkItemLink | undefined> {
        const linkTypeEnd = await this.getWitLinkTypeEnd(context);
        const linkType = linkTypeEnd.linkType;

        if (linkType && linkType.isActive && linkType.topology !== "Unknown" && !(linkType.isOneToMany && linkTypeEnd.isForwardLink)) {
            const remoteLinkContext: RemoteLinkContext = {
                remoteHostId: this.remoteHostId!,
                remoteHostName: this.remoteHostName!,
                remoteHostUrl: this.remoteHostUrl!,
                remoteProjectId: this.remoteProjectId!
            };
            return WorkItemLink.create(context, linkTypeEnd, this.getTargetId(), this.getComment(), false, remoteLinkContext);
        }
    }
}

export class ExternalLink extends Link {
    public static create(registeredLinkType?: string, artifactUri?: string, comment?: string): ExternalLink {
        return new ExternalLink({
            OriginalName: registeredLinkType,
            FilePath: artifactUri,
            Comment: comment || "",
            FldID: DalFields.BISURI,
            AddedDate: FutureDate,
            RemovedDate: FutureDate
        });
    }

    public constructor(linkData: ILinkInfo) {
        super(linkData);
        this.baseLinkType = "ExternalLink";
    }

    public equals(link: ILink): boolean {
        return (
            super.equals(link) &&
            link instanceof ExternalLink &&
            link.linkData.OriginalName === this.linkData.OriginalName &&
            link.linkData.FilePath === this.linkData.FilePath
        );
    }

    public getArtifactLinkType() {
        return this.linkData.OriginalName;
    }

    public getLinkedArtifactUri() {
        return this.linkData.FilePath;
    }

    public async clone(context: IVssPageContext, target: IWorkItem): Promise<ExternalLink | undefined> {
        return ExternalLink.create(this.getArtifactLinkType(), this.getLinkedArtifactUri(), this.getComment());
    }
}

export class Hyperlink extends Link {
    public static create(artifactUri?: string, comment?: string): Hyperlink {
        return new Hyperlink({
            OriginalName: "",
            FilePath: artifactUri,
            Comment: comment || "",
            FldID: DalFields.LinkedFiles,
            AddedDate: FutureDate,
            RemovedDate: FutureDate
        });
    }

    public equals(link: ILink) {
        return super.equals(link) && link instanceof Hyperlink && link.linkData.FilePath === this.linkData.FilePath;
    }

    public async clone(context: IVssPageContext, target: IWorkItem): Promise<Hyperlink | undefined> {
        const now = new Date();
        return new Hyperlink({
            ...this.linkData,
            CreationDate: now,
            LastWriteDate: now,
            AddedDate: FutureDate,
            RemovedDate: FutureDate
        });
    }

    public getLinkType(): string {
        return this.linkData.OriginalName || RegisteredLinkTypeNames.Hyperlink;
    }
}

export class Attachment extends Link {
    public static create(fileName: string, fileGuid: string, fileSize: number, comment: string | undefined): Attachment {
        const now = new Date();
        return new Attachment({
            OriginalName: fileName,
            FilePath: fileGuid,
            Comment: comment || "",
            ExtID: 0,
            FldID: DalFields.AttachedFiles,
            Length: fileSize,
            CreationDate: now,
            LastWriteDate: now,
            AddedDate: FutureDate,
            RemovedDate: FutureDate
        });
    }

    public constructor(linkData: ILinkInfo) {
        super(linkData);
        this.baseLinkType = "Attachment";
    }

    public equals(link: ILink) {
        return super.equals(link) && link instanceof Attachment && link.linkData.FilePath === this.linkData.FilePath;
    }

    public async clone(context: IVssPageContext, target: IWorkItem): Promise<Attachment | undefined> {
        return Attachment.create(this.linkData.OriginalName!, this.linkData.FilePath!, this.linkData.Length!, this.linkData.Comment);
    }
}
